import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Stack } from "@financeable-com-au/financeable-ui";
import {
  ALLOWED_ASSET_DOCUMENT_TYPE,
  MAX_DOCUMENT_UPLOAD_SIZE,
  SECTION_TYPE,
  SUCCESS_MESSAGE,
  ERRORS_MESSAGE,
} from "@/constants";
import { IDispatchResponse } from "@/utils/interface";
import { openSnackbar } from "@/store/slices/snackbarSlice";
// @ts-expect-error: ignoring as it is existing js file
import { requestAssetInvoice, uploadAssetInvoice, userDetailsSelector } from "@/store/slices/applicationFormSlice";
import { Asset, type Document } from "@/store/slices/types/applicationFormSlice";
import { ICurrentTemplate } from "@/store/slices/types/templatesSlice";
import { getErrorToastPayload, getSuccessToastPayload } from "@/utils/commonFunctions";
import { getMissingRequiredFields } from "@/utils/requiredFieldRules";
import { ASSET_INVOICE_REQUIRED_FIELDS } from "./assetRequiredFields";
import FileUploadDropzone from "../FileUpload/FileUploadDropzone";

interface IAssetInvoice {
  asset: Asset;
  applicationId: string;
  isDisableEdit: boolean;
  /** Which asset inputs are on screen, so only fields the user can actually fill in are required. */
  isGlassesGuideMode: boolean;
  canEnterRegistration: boolean;
  canEnterVin: boolean;
}

const AssetInvoice = ({
  asset,
  applicationId,
  isDisableEdit,
  isGlassesGuideMode,
  canEnterRegistration,
  canEnterVin,
}: IAssetInvoice) => {
  const dispatch = useDispatch();
  const { assetDocumentUploadLoading, requestAssetInvoiceLoading } = useSelector(userDetailsSelector) as {
    assetDocumentUploadLoading: boolean;
    requestAssetInvoiceLoading: boolean;
  };
  const documents = asset?.documents;
  const existingInvoice = documents?.find((document: Document) => document.documentType === "Asset invoice");

  const missingRequiredFields = useMemo(
    () =>
      getMissingRequiredFields(ASSET_INVOICE_REQUIRED_FIELDS, {
        asset,
        isGlassesGuideMode,
        canEnterRegistration,
        canEnterVin,
      }),
    [asset, isGlassesGuideMode, canEnterRegistration, canEnterVin],
  );

  const requestInvoiceHandler = async (email: string): Promise<boolean> => {
    if (isDisableEdit || requestAssetInvoiceLoading || missingRequiredFields.length > 0) return false;

    const res = (await dispatch(
      requestAssetInvoice({
        applicationId,
        assetId: asset._id,
        email,
      }),
    )) as IDispatchResponse;
    if (res?.error) {
      dispatch(openSnackbar(getErrorToastPayload(res, "Error in requesting asset invoice")));

      return false;
    }

    dispatch(openSnackbar(getSuccessToastPayload(res, SUCCESS_MESSAGE.assetInvoiceRequestSent)));
    return true;
  };

  const saveInvoiceFilesHandler = (files: File[]) => {
    const newSelectedFiles = [];
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (ALLOWED_ASSET_DOCUMENT_TYPE.includes(file.type)) {
        if (file.size <= MAX_DOCUMENT_UPLOAD_SIZE) {
          newSelectedFiles.push(file);
        }
      }
    }
    if (newSelectedFiles?.length) {
      newSelectedFiles.forEach((file) => {
        formData.append("file", file);
      });
      formData.append("applicationId", applicationId);
      formData.append("assetId", asset._id);
      formData.append("name", "invoice");

      dispatch(uploadAssetInvoice({ formData })).then((res: IDispatchResponse) => {
        if (res?.error) {
          dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
        } else {
          dispatch(openSnackbar(getSuccessToastPayload(res, SUCCESS_MESSAGE.fileUploadSuccess)));
        }
      });
    }
  };

  return (
    <Stack>
      <FileUploadDropzone
        isDisabled={isDisableEdit}
        isAssetDocumentUploadLoading={assetDocumentUploadLoading}
        isMultiple={false}
        saveFilesHandler={saveInvoiceFilesHandler}
        onRequestInvoice={requestInvoiceHandler}
        isRequestInvoiceLoading={requestAssetInvoiceLoading}
        requestInvoiceMissingFields={missingRequiredFields}
        fileData={existingInvoice || ({} as Document | ICurrentTemplate)}
        sectionType={SECTION_TYPE.ASSET_INVOICE}
      />
    </Stack>
  );
};

export default AssetInvoice;
